import { ArrowRight, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ToastIcons } from "@/helpers/toast.helpers";

interface EvaluationCompleteToastProps {
  variant: "success" | "error";
  title: string;
  description?: string;
  viewLabel: string;
  onView: () => void;
  onDismiss: () => void;
}

/**
 * Custom toast body for the "accuracy run finished" notification.
 *
 * The styled `showSuccessToast`/`showErrorToast` factories only have one
 * action slot — a default dismiss-X, or a single custom CTA, never both. This
 * notification needs both a "View results" action and an explicit close, so
 * it renders its own content via `showToast()`'s passthrough instead,
 * reusing `ToastIcons` and the caller-supplied `TOAST_STYLES`/`TOAST_CUSTOM_CLASS_NAMES`
 * for the outer card so it still looks like every other toast in the app.
 */
export const EvaluationCompleteToast = ({
  variant,
  title,
  description,
  viewLabel,
  onView,
  onDismiss,
}: EvaluationCompleteToastProps) => (
  <div className="flex w-full items-start gap-3">
    {ToastIcons[variant]}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-[var(--dash-ink,var(--text-high-emphasis))]">{title}</p>
      {description && (
        <p className="mt-0.5 text-sm text-[var(--dash-muted,var(--text-medium-emphasis))]">
          {description}
        </p>
      )}
      {/* Both controls sit inside a toast card, so they stay bare text/icon:
          `h-auto p-0` drops the button geometry and the transparent hovers
          drop ghost's own hover fill, leaving the colour change as the only
          feedback. `has-[>svg]:px-0` is needed on top of `p-0`: the default
          size adds `has-[>svg]:px-3` for buttons with an icon, and that
          variant outranks `p-0` — without it the link sits 12px right of the
          title it should line up with. */}
      <Button
        type="button"
        variant="ghost"
        onClick={onView}
        className="mt-2 inline-flex h-auto items-center gap-1 p-0 has-[>svg]:px-0 text-sm font-medium text-[var(--dash-accent,var(--primary-500))] transition-colors hover:bg-transparent hover:text-[var(--dash-accent-ink,var(--primary-600))] dark:hover:bg-transparent"
      >
        {viewLabel}
        <ArrowRight className="size-3.5" aria-hidden="true" />
      </Button>
    </div>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onDismiss}
      aria-label="Dismiss"
      className="-mr-1 size-auto shrink-0 self-start p-0 text-[var(--dash-muted,var(--icon-grey-1))] transition-colors hover:bg-transparent hover:text-[var(--dash-ink,var(--text-high-emphasis))] dark:hover:bg-transparent"
    >
      <X className="size-4" aria-hidden="true" />
    </Button>
  </div>
);
